import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Popover, Space, Typography } from 'antd'
import { useIntl } from 'react-intl'

import IntlMessages from '../../util/IntlMessages'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { selectAuthUserSlice } from '../../store/auth-user-slice'
import { selectPendingHint, setPendingHint } from '../../store/whats-news-slice'
import { useCrispYield } from '../../hooks/use-crisp-yield'
import { clearPendingWhatsNewHintId } from '../../util/whats-new-hint-storage'
import { track } from '../../services/analytics/analytics'
import { VeeMark } from './vee-mark'
import { VeePanel, VeeView } from './vee-panel'
import { useVeeChat } from './hooks'

interface IVeeAssistantProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
}

interface ILauncherHint {
  title: React.ReactNode
  body?: React.ReactNode
  action?: React.ReactNode
  onDismiss: () => void
}

// Chat state lives here, above the panel, so closing the panel keeps the conversation.
const VeeAssistant: React.FC<IVeeAssistantProps> = ({ isOpen, onOpenChange }) => {
  const { formatMessage } = useIntl()
  const { authUser } = useAppSelector(selectAuthUserSlice)
  const pendingHint = useAppSelector(selectPendingHint)
  const dispatch = useAppDispatch()
  const [view, setView] = useState<VeeView>('home')
  // A ref, not state: keystrokes mirror the draft here without re-rendering the panel.
  const draftRef = useRef('')
  const chat = useVeeChat()

  const launcherRef = useRef<HTMLButtonElement | null>(null)
  const wasOpenRef = useRef(false)

  useEffect(() => {
    if (!isOpen && wasOpenRef.current) {
      launcherRef.current?.focus()
    }
    wasOpenRef.current = isOpen
  }, [isOpen])

  const openPanel = () => {
    onOpenChange(true)
  }

  // Closing ends the session: reopening lands on home, the conversation stays in history.
  const closePanel = useCallback(() => {
    onOpenChange(false)
    draftRef.current = ''
    chat.newChat()
    setView('home')
  }, [onOpenChange, chat.newChat])

  // Yielding to Crisp only hides the panel: view, conversation and draft stay
  // in this component, so reopening resumes exactly where the user was.
  const yieldPanel = useCallback(() => {
    onOpenChange(false)
  }, [onOpenChange])

  useCrispYield({ isOpen, onYield: yieldPanel })

  const dismissNewsHint = () => {
    clearPendingWhatsNewHintId(authUser.id)
    dispatch(setPendingHint(null))
  }

  // The launcher carries the last closed announcement, and nothing else.
  const hint: ILauncherHint | null = pendingHint
    ? {
        title: pendingHint.title,
        body: pendingHint.subtitle,
        action: pendingHint.linkUrl && (
          <Button
            type="primary"
            size="small"
            href={pendingHint.linkUrl}
            target={pendingHint.openLinkInSameTab ? '_self' : '_blank'}
            rel="noopener noreferrer"
            onClick={() => {
              track('LEARN_MORE_BUTTON_CLICKED', { source: 'vee-hint' })
              dismissNewsHint()
            }}
          >
            {pendingHint.linkText || <IntlMessages id="app.learnMore" />}
          </Button>
        ),
        onDismiss: dismissNewsHint,
      }
    : null

  return (
    <>
      {!isOpen && (
        <div className="vee-launcher-wrap">
          {/* Hover-out also fires onOpenChange(false); the news hint is persisted, so only explicit clicks may clear it. */}
          <Popover
            open={Boolean(hint)}
            placement="bottomLeft"
            content={
              hint && (
                <Space direction="vertical" size={4} style={{ maxWidth: 260 }}>
                  <Typography.Text strong>{hint.title}</Typography.Text>
                  {hint.body && <Typography.Text>{hint.body}</Typography.Text>}
                  <Space>
                    {hint.action}
                    <Button type="text" size="small" onClick={hint.onDismiss}>
                      <IntlMessages id="veeAssistant.meetDismiss" />
                    </Button>
                  </Space>
                </Space>
              )
            }
          >
            <div className={hint ? 'vee-launcher-spotlight' : undefined}>
              <button ref={launcherRef} type="button" className="vee-launcher" onClick={openPanel} aria-label={formatMessage({ id: 'veeAssistant.launcher' })}>
                <VeeMark size={32} />
                <span>{formatMessage({ id: 'veeAssistant.name' })}</span>
              </button>
            </div>
          </Popover>
        </div>
      )}
      {isOpen && (
        <VeePanel
          view={view}
          setView={setView}
          chat={chat}
          draft={draftRef.current}
          onDraftChange={(value) => {
            draftRef.current = value
          }}
          onClose={closePanel}
        />
      )}
    </>
  )
}

export default VeeAssistant
